import classnames from "classnames";
import HtmlToReact, { Parser } from "html-to-react";
import get from "lodash-es/get";
import React, { PureComponent, ReactElement, ReactNode, Suspense } from "react";
import Helmet from "react-helmet";
import {
  BannerSlider,
  Collapse,
  EmbedVideo,
  LazyloadImage,
  PrismicPopin,
  StoreLocatorPrismic,
  T2sSlider,
} from "src/components";
import { getSmallerProductImageSrc } from "src/services/image.service";
import styled from "styled-components";

import { isUrlInternalAbsolute } from "../../../lib/utils/urls";
import { IData as IDataV2 } from "../../../pages/CMS/PageCmsCustom/types";
import { fetchPrismic } from "../../../services/fetchPrismic.service";
import { mapPrismicProductToProduct } from "../../../services/mapperService";
import theme from "../../../theme";
import "../../../theme/cssLmdw/lmdw-styles.css";
import "../../../theme/cssLmdw/responsive-grid.css";
import {
  ICatalogProducts,
  IPrimary,
  IPrimaryModal,
  IPrismicProduct,
  IPrismicSlice,
  ISlider,
} from "../../../types/prismic";
import { IProduct } from "../../../types/product";
import Countdown from "../../Countdown";
import Hyperlink from "../../Hyperlink";
import MiniProductsCarousel from "../../MiniProductsCarousel";
import ProductAddButton from "../../ProductAddButton";

import { giftFinder } from "./scripts";

export type ICMSId = string;
export type ICMSType = string;

const WrapperPageCMS = styled.div`
  &.col1-layout {
    padding: ${theme.margins.paddingPageBig};
  }
  &.event-page {
    padding: 0;
    position: relative;
  }
`;
const Title = styled.h1``;

interface IHtmlToReactNode {
  attribs: { [key: string]: string };
  data?: string;
  name: string;
  type: string;
  children?: Array<{ data: string }>;
  className?: string;
}

interface IData {
  // tslint:disable-next-line: no-any
  title?: any;
  preformatted_content?: string;
  body?: IPrismicSlice[];
  content_heading?: string;
  magento_template?: string;
  css_content?: string;
  js_content?: string;
}
interface IProps {
  id: ICMSId;
  type: ICMSType;
  data?: IData & IDataV2;
  setTabTitle?: (tabTitle: string | null) => void;
}
interface IState {
  data: IData | null;
}

class PageCMSLoader extends PureComponent<IProps, IState> {
  public state: IState = {
    data: null,
  };
  private mounted: boolean = false;

  public componentDidUpdate(oldProps: IProps): void {
    if (oldProps.id !== this.props.id) {
      this.componentDidChange();
    }
  }

  public componentDidMount(): void {
    this.mounted = true;

    this.componentDidChange();
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public componentDidChange = async (): Promise<void> => {
    const { id, type, setTabTitle } = this.props;

    try {
      let data: IData | undefined;

      if (!this.props.data) {
        const response = await fetchPrismic<IData>(id, type, {
          graphQuery: ``,
        });

        data = response?.data;
      } else {
        data = this.props.data;
      }

      if (this.mounted) {
        if (setTabTitle && type === "page_cms_import") {
          let title: string | null = null;
          if (typeof data?.title === "string") {
            title = data?.title;
          } else if (Array.isArray(data?.title)) {
            title = data?.title?.[0]?.text || null;
          }
          setTabTitle(title);
        }
        this.setState({
          data: data || null,
        });

        if (id === "homepage-gift-block") {
          giftFinder();
        }
      }
    } catch (e) {
      if (this.mounted) {
        this.setState({
          data: null,
        });
      }
    }
  };

  public render(): ReactNode {
    const { data } = this.state;

    if (!data) {
      return null;
    }

    return (
      <>
        {data.css_content && (
          <Helmet>
            <style>{data.css_content}</style>
          </Helmet>
        )}
        <WrapperPageCMS
          id={
            data.magento_template === "page_cms_custom" ? "int_col2" : undefined
          }
          className={
            data.magento_template === "event_page"
              ? "event-page"
              : "col1-layout"
          }
        >
          {data.magento_template !== "event_page" &&
            get(data, "content_heading") && (
              <div className="page-title">
                <Title>{data.content_heading}</Title>
              </div>
            )}
          {data.preformatted_content &&
            this.transformPrismicHTML(data.preformatted_content)}
        </WrapperPageCMS>
      </>
    );
  }

  public transformPrismicHTML = (
    htmlInput: string
  ): ReactElement<{}> | null => {
    try {
      const htmlToReactParser = new Parser();
      const isValidNode = (): boolean => {
        return true;
      };

      const processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(
        React
      );

      const processingInstructions = [
        // Handle prismic link transformation
        {
          processNode: (
            node: IHtmlToReactNode,
            children: IHtmlToReactNode[]
          ): ReactNode => {
            node.attribs.className = node.attribs.class;
            delete node.attribs.class;

            const regexBaseUrl = /^(https?:\/\/)?(www\.)?whisky.fr/i;
            const to = isUrlInternalAbsolute(node.attribs.href)
              ? node.attribs.href.replace(regexBaseUrl, "")
              : node.attribs.href;

            return (
              <Hyperlink to={to} {...node.attribs}>
                {children}
              </Hyperlink>
            );
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean =>
            node && node.attribs && !!node.attribs.href,
        },

        // Handle prismic lazyload images transformation
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              const altText =
                !node.attribs.alt || node.attribs.alt === ""
                  ? "lazyload image"
                  : node.attribs.alt;

              node.attribs.alt = altText;
              node.attribs.className = node.attribs.class;
              delete node.attribs.class;

              // console.log(node.name, ": ", node.attribs);

              return (
                <LazyloadImage height="auto" key="node.attribs.src">
                  <img {...node.attribs} alt={altText} />
                </LazyloadImage>
              );
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error(
                `Prismic - lazyload image error: `,
                node.name,
                node
              );
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean =>
            node && node.attribs && !!node.attribs.src,
        },

        // Handle prismic carousel transformation
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              if (this.state.data?.body && node.data) {
                const sliceCarousel: IPrismicSlice = this.state.data.body.filter(
                  (item) =>
                    item.slice_type === "catalogue_produits" &&
                    `{{widget type="awislider/widget_block" block_id="${item.primary?.catalog_product_id}"}}`.trim() ===
                      node?.data?.trim()
                )[0];

                if (!sliceCarousel.items?.length) {
                  throw Error;
                }

                const carouselProducts: IPrismicProduct[] = sliceCarousel.items;

                const products: IProduct[] = mapPrismicProductToProduct(
                  carouselProducts
                ).map((product) => {
                  // replace product image by a pre-existing smaller version (169x225)
                  product.image = getSmallerProductImageSrc(product.image);

                  return product;
                });

                const maxSlides = sliceCarousel.primary?.max_slides;

                return (
                  <MiniProductsCarousel
                    products={products}
                    maxSlides={maxSlides}
                  />
                );
              }
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error(
                `Prismic - carousel data error: `,
                get(this.state.data, "body[0].items")
              );

              return <MiniProductsCarousel />;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            const carouselRegex = /\{\{widget type="awislider\/widget_block" block_id="(.+)"\}\}/g;

            const carouselRegexMatch = node?.data?.match(carouselRegex);

            return carouselRegexMatch ? carouselRegexMatch.length > 0 : false;
          },
        },
        // Handle AddToCart button
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              if (this.state.data?.body && node.data) {
                const sliceAddToCart: IPrismicSlice = this.state.data.body.filter(
                  (item) =>
                    item.slice_type === "add_to_cart" &&
                    `{{addToCart id="${item.primary?.id}"}}` ===
                      node.data?.trim()
                )[0];

                if (
                  sliceAddToCart.primary === undefined ||
                  sliceAddToCart.primary.product === undefined
                ) {
                  throw Error;
                }

                const prismicProduct: ICatalogProducts =
                  sliceAddToCart.primary.product;

                const product: IProduct = mapPrismicProductToProduct([
                  prismicProduct,
                ])[0];

                return (
                  <ProductAddButton
                    product={product}
                    canBeAddedToCart={true}
                    className={sliceAddToCart.primary?.classnames}
                    canEditQuantity={sliceAddToCart.primary?.can_edit_quantity}
                  />
                );
              }
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error(
                `Prismic - addToCart data error: `,
                this.state.data?.body
              );

              return null;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            const addToCartRegex = /{{addToCart id=(.*)}}/g;
            const addToCartRegexMatch = node?.data?.match(addToCartRegex);

            return addToCartRegexMatch ? addToCartRegexMatch.length > 0 : false;
          },
        },
        // Handle Store locator map
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              if (this.state.data?.body && node.data) {
                const sliceStoreLocator = this.state.data.body.filter(
                  (item) =>
                    item.slice_type === "storeLocator" &&
                    `{{storeLocator id="${item.primary?.id}"}}` ===
                      node.data?.trim()
                )[0];

                if (!sliceStoreLocator.primary) {
                  throw Error;
                }

                return (
                  <Suspense fallback={<div />}>
                    <StoreLocatorPrismic data={sliceStoreLocator.primary} />
                  </Suspense>
                );
              }
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error(
                `Prismic - storeLocator data error: `,
                this.state.data?.body
              );

              return null;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            const storeLocatorRegex = /{{storeLocator id=(.*)}}/g;
            const storeLocatorRegexMatch = node?.data?.match(storeLocatorRegex);

            return storeLocatorRegexMatch
              ? storeLocatorRegexMatch.length > 0
              : false;
          },
        },
        // Handle Embed Youtube player
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              if (this.state.data?.body && node.data) {
                const sliceEmbedVideo = this.state.data.body.filter(
                  (item) =>
                    item.slice_type === "embed_video" &&
                    `{{embedVideo id="${item.primary?.id}"}}` ===
                      node.data?.trim()
                )[0];

                if (!sliceEmbedVideo?.primary) {
                  throw Error;
                }

                const dataVideo = sliceEmbedVideo.primary as IPrimary;

                // console.log("Youtube", this.state.data?.body, dataVideo);

                // console.log("Youtube", this.state.data?.body, dataVideo);

                return <EmbedVideo data={dataVideo} />;
              }
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error(
                `Prismic - Embed Youtube data error: `,
                this.state.data?.body
              );

              return null;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            const embedVideoRegex = /{{embedVideo id=(.*)}}/g;
            const embedVideoRegexMatch = node?.data?.match(embedVideoRegex);

            return embedVideoRegexMatch
              ? embedVideoRegexMatch.length > 0
              : false;
          },
        },
        // Handle Collapse sections
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              if (this.state.data?.body && node.data) {
                const sliceCollapse = this.state.data.body.filter(
                  (item) =>
                    item.slice_type === "collapse" &&
                    `{{collapse id="${item.primary?.id}"}}` ===
                      node.data?.trim()
                )[0];

                if (!sliceCollapse?.primary) {
                  throw Error;
                }

                const dataCollapse = sliceCollapse.primary as IPrimary;

                return <Collapse data={dataCollapse} />;
              } else {
                // tslint:disable-next-line:no-console
                console.error(
                  `Prismic - Collapse sections data error: there is an issue with the tag {{collapse}}`
                );

                return null;
              }
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error(
                `Prismic - Collapse sections data error: `,
                this.state.data?.body
              );

              return null;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            const collapseRegex = /{{collapse id=(.*)}}/g;
            const collapseRegexMatch = node?.data?.match(collapseRegex);

            return collapseRegexMatch ? collapseRegexMatch.length > 0 : false;
          },
        },
        // Handle Banner slider
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              if (this.state.data?.body && node.data) {
                const sliceSlider = this.state.data.body.filter(
                  (item) =>
                    item.slice_type === "slider" &&
                    `{{slider id="${item.primary?.id}"}}` === node.data?.trim()
                )[0] as ISlider;

                return <BannerSlider data={sliceSlider} />;
              } else {
                // tslint:disable-next-line:no-console
                console.error(
                  `Prismic - Banner slider data error: there is an issue with the tag {{slider}}`
                );

                return null;
              }
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error(
                `Prismic - Banner slider data error: `,
                this.state.data?.body
              );

              return null;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            const sliderRegex = /{{slider id=(.*)}}/g;
            const sliderRegexMatch = node?.data?.match(sliderRegex);

            return sliderRegexMatch ? sliderRegexMatch.length > 0 : false;
          },
        },
        // Handle modal
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              if (this.state.data?.body && node.data) {
                const sliceModal = this.state.data.body.filter(
                  (item) =>
                    item.slice_type === "modal" &&
                    `{{modal id="${item.primary?.id}"}}` === node.data?.trim()
                )[0].primary as IPrimaryModal;

                return <PrismicPopin data={sliceModal} />;
              } else {
                // tslint:disable-next-line:no-console
                console.error(
                  `Prismic - modal data error: there is an issue with the tag {{modal}}`
                );

                return null;
              }
            } catch (e) {
              // tslint:disable-next-line:no-console
              console.error(
                `Prismic - modal data error: `,
                this.state.data?.body
              );

              return null;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            const modalRegex = /{{modal id=(.*)}}/g;
            const modalRegexMatch = node?.data?.match(modalRegex);

            return modalRegexMatch ? modalRegexMatch.length > 0 : false;
          },
        },
        // Handle onClick transformation
        {
          processNode: (
            node: IHtmlToReactNode,
            children: IHtmlToReactNode[]
          ): ReactNode => {
            node.attribs.className = node.attribs.class;
            delete node.attribs.onclick;
            delete node.attribs.class;

            interface IItem {
              name?: string;
              data?: string;
              attribs?: {
                src: string;
                className: string | undefined;
                alt: string | undefined;
              };
            }

            return (
              <button {...node.attribs}>
                {node!.children!.map(
                  ({ name, data, attribs }: IItem, index: number): ReactNode =>
                    name! === "img" ? (
                      <img
                        alt={attribs && attribs.alt ? attribs.alt : ""}
                        src={attribs!.src}
                        className={
                          attribs && attribs.className
                            ? attribs.className
                            : undefined
                        }
                        key={`item-${index}`}
                      />
                    ) : (
                      data !== " " && <div key={`item-${index}`}>{data}</div>
                    )
                )}
              </button>
            );
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean =>
            node &&
            node.attribs &&
            !!node.attribs.onclick &&
            node.name === "button",
        },
        // Handle countdown transformation
        {
          processNode: (
            node: IHtmlToReactNode,
            children: IHtmlToReactNode[]
          ): ReactNode => {
            try {
              if (get(this.state.data, "body[0].primary")) {
                return (
                  <Countdown
                    key={"this.state.data.countdown_id"}
                    data={get(this.state.data, "body[0].primary")}
                  />
                );
              }
            } catch (e) {
              return null;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            return node && node.name === "countdown";
          },
        },
        // Handle T2S recommandation slider
        {
          processNode: (node: IHtmlToReactNode): ReactNode => {
            try {
              if (this.state.data?.body && node.data) {
                const tagType = "sliderT2sReco";
                const sliceType = "slider_t2s_reco";
                const tag = node.data.trim();
                const slices = this.state.data?.body;

                const slice = slices.filter(
                  (item) =>
                    item.slice_type === sliceType &&
                    `{{${tagType} id="${item.primary?.id}"}}` === tag
                )[0];

                const title = slice.primary?.t2s_title;
                const spaceId = slice.primary?.t2s_space_id;
                const showPrice = slice.primary?.t2s_show_price;
                const showRating = slice.primary?.t2s_show_rating;
                const maxSlides = slice.primary?.max_slides;
                const dots = slice.primary?.has_dots;
                const className = classnames(
                  tagType,
                  slice.primary?.classnames
                );

                if (!spaceId) {
                  return null;
                }

                const options = {
                  spaceId,
                  className,
                  title,
                  dots,
                  showPrice,
                  showRating,
                  maxSlides,
                };

                return <T2sSlider options={options} />;
              } else {
                return null;
              }
            } catch (e) {
              return null;
            }
          },
          shouldProcessNode: (node: IHtmlToReactNode): boolean => {
            const tagRegex = /{{sliderT2sReco id=(.*)}}/g;
            const tags = node?.data?.match(tagRegex);

            return !!tags?.length || false;
          },
        },
        // Otherwise don't process nodes
        {
          processNode: processNodeDefinitions.processDefaultNode,
          shouldProcessNode: (): boolean => {
            return true;
          },
        },
      ];

      return htmlToReactParser.parseWithInstructions(
        htmlInput,
        isValidNode,
        processingInstructions
      );
    } catch (e) {
      return null;
    }
  };
}

export default PageCMSLoader;
